/*
 * WHICH ENGLISH THIS READER PREFERS — ONE CHOICE, APPLIED WHEREVER IT EXISTS.
 *
 * A reader who picks the King James wants the King James, not the King James in the book they
 * happened to be reading when they picked it. So the preference is one value for the whole
 * library, and every passage that HAS that translation shows it. A passage that does not falls
 * back to its own first rendering, because the alternative is a blank verse or — far worse —
 * another translator's words under the chosen name.
 *
 * STORED AS THE TRANSLATION'S NAME, AND THAT IS THE WHOLE REASON THIS WORKS.
 *
 * The rendering ids are positional — `primary`, `second`, `third` — and they mean different
 * things in different texts: `third` is the JPS in the Hebrew Bible and nothing at all in the
 * Quran. Storing one of those and applying it across the library would show a reader whatever
 * happened to be third wherever they went, which is not a preference, it is a coincidence.
 *
 * A name is the same translation everywhere it appears. "King James Version, 1769" is the King
 * James in the Hebrew Bible and in the New Testament, so choosing it once reaches both — which is
 * exactly what was asked for. It is also what a reader would say out loud if you asked them which
 * translation they wanted, and a preference the reader could not describe is a preference they
 * cannot check.
 *
 * The cost is that renaming an edition drops the preference. That is the right way round: a
 * changed name is a changed claim about whose words these are, and falling back to the default is
 * honest where silently mapping the old name onto the new text would not be.
 *
 * GUARDED THE SAME WAY `arrived.ts` IS. A browser that forbids storage — private mode, or none in
 * a test — falls back to the default rendering rather than throwing. Losing a preference is a
 * small harm; a reader unable to open a passage is not.
 */
const KEY = 'vv.translation';

/*
 * EVERY PASSAGE ON SCREEN CHANGES AT ONCE, ACROSS NODES.
 *
 * "Applies to all" is not only about the next passage a reader opens. A node's panel may be
 * showing thirty verses, the reader may have a second node open beside it, and picking a
 * translation on one of them has to move all of them — otherwise the same text is shown in two
 * Englishes on one screen, which is worse than not offering the choice at all.
 *
 * Local component state cannot do that: each passage would read the preference once and keep its
 * own copy. So the value lives here, components subscribe, and one write notifies every reader of
 * it. `useSyncExternalStore` is the React-sanctioned way to read a value that lives outside React
 * and can change beneath it.
 *
 * The `storage` event is subscribed too, so a reader who changes translation in one tab sees it
 * in another. Browsers fire it only for OTHER tabs, which is exactly the missing half — the tab
 * that made the change is covered by the local listeners.
 */
type Listener = () => void;
const listeners = new Set<Listener>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to changes of the preferred translation. Returns the unsubscribe. */
export function subscribeTranslation(listener: Listener): () => void {
  listeners.add(listener);
  const fromAnotherTab = (event: StorageEvent): void => { if (event.key === KEY) listener(); };
  if (typeof window !== 'undefined') window.addEventListener('storage', fromAnotherTab);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', fromAnotherTab);
  };
}

/** The translation this reader chose, by name, or undefined if they have not chosen. */
export function preferredTranslation(): string | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    const stored = localStorage.getItem(KEY);
    /* A blank string is not a translation anybody named, and treating it as one would search
       every passage for a rendering called "". */
    return stored?.trim() ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** Remember a choice for the whole library. A refused write means the default shows next time. */
export function rememberTranslation(name: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (!name.trim()) return;
    localStorage.setItem(KEY, name);
    announce();
  } catch {
    /* storage refused — the reader keeps their choice for this session and not beyond it. */
  }
}

/** Forget the choice, so every text goes back to its own first rendering. */
export function forgetTranslation(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(KEY);
    announce();
  } catch {
    /* storage refused — there was nothing that would have outlived this session anyway. */
  }
}
